export const precisionMath = value => parseFloat(value.toFixed(10));

export const checkValue = (value, min, max) => {
  if (value < min) {
    return min;
  }
  if (value > max) {
    return max;
  }
  return value;
};

const nativeRequestAnimationFrame =
  window.requestAnimationFrame ||
  window.webkitRequestAnimationFrame ||
  window.mozRequestAnimationFrame ||
  window.oRequestAnimationFrame ||
  window.msRequestAnimationFrame;

export const cancelAnimationFramePolyfill =
  window.cancelAnimationFrame ||
  window.webkitCancelAnimationFrame ||
  window.mozCancelAnimationFrame ||
  window.oCancelAnimationFrame ||
  window.msCancelAnimationFrame ||
  window.clearTimeout;

export const getWindow = node => {
  if (node === node.window) {
    return node;
  }

  return node.nodeType === 9 ? node.defaultView || node.parentWindow : null;
};

const fallback = (context, node) => {
  if (node) {
    do {
      if (node === context) {
        return true;
      }
      // eslint-disable-next-line no-cond-assign
    } while ((node = node.parentNode));
  }
  return false;
};

// HTML DOM and SVG DOM may have different support levels,
// so we need to check on context instead of a document root element.
const contains = (context, node) => {
  if (context.contains) {
    return context.contains(node);
  }

  if (context.compareDocumentPosition) {
    // eslint-disable-next-line no-bitwise
    return context === node || !!(context.compareDocumentPosition(node) & 16);
  }

  return fallback(context, node);
};

export const getOffset = node => {
  const doc = node.ownerDocument;
  const win = getWindow(doc);
  const docElem = doc && doc.documentElement;

  let box = {
    top: 0,
    left: 0,
    height: 0,
    width: 0
  };

  if (!doc) {
    return null;
  }

  // Make sure it's not a disconnected DOM node
  if (!contains(docElem, node)) {
    return box;
  }

  if (node.getBoundingClientRect !== undefined) {
    box = node.getBoundingClientRect();
  }

  if ((box.width || box.height) && docElem && win) {
    box = {
      top:
        box.top +
        (win.pageYOffset || docElem.scrollTop) -
        (docElem.clientTop || 0),
      left:
        box.left +
        (win.pageXOffset || docElem.scrollLeft) -
        (docElem.clientLeft || 0),
      width: (box.width === null ? node.offsetWidth : box.width) || 0,
      height: (box.height === null ? node.offsetHeight : box.height) || 0
    };
  }

  return box;
};

export const getWidth = node => {
  const win = getWindow(node);

  if (win) {
    return win.innerWidth;
  }

  const offset = getOffset(node);

  return offset ? offset.width : 0;
};

export const getHeight = node => {
  const win = getWindow(node);

  if (win) {
    return win.innerHeight;
  }

  return getOffset(node).height;
};

function createRequestAnimationFrame() {
  function emptyFunction() {}

  let lastTime = 0;

  // eslint-disable-next-line no-underscore-dangle
  function _setTimeout(callback) {
    const currTime = Date.now();
    const timeDelay = Math.max(0, 16 - (currTime - lastTime));
    lastTime = currTime + timeDelay;
    return window.setTimeout(() => {
      callback(Date.now());
    }, timeDelay);
  }

  /**
   * Here is the native and polyfill version of requestAnimationFrame.
   * Please don't use it directly and use requestAnimationFrame module instead.
   */
  const requestAnimationFrame =
    (nativeRequestAnimationFrame && nativeRequestAnimationFrame.bind(window)) ||
    _setTimeout;

  // Works around a rare bug in Safari 6 where the first request is never invoked.
  requestAnimationFrame(emptyFunction);

  return requestAnimationFrame;
}

export const requestAnimationFramePolyfill = createRequestAnimationFrame();
